import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import MessageRow from "./MessageRow";
import Ticks from "./Ticks";
import { MentionUserSheet } from "./MentionUserSheet";
import { TABS } from "../constants/tabs";


/* ─── helpers ─────────────────────────────────────────────────────────────── */
const isSameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth()    === db.getMonth()    &&
    da.getDate()     === db.getDate()
  );
};

const formatDayLabel = (ts) => {
  const date      = new Date(ts);
  const today     = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, today))     return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    weekday: "short", day: "numeric", month: "short",
    year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

const formatTime = (ts) => {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (secs) => {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const isMsgSeen = (m) =>
  m.raw?.message?.status?.seen    === true ||
  !!m.raw?.message?.status?.seenAt ||
  m.raw?.message?.seen            === true ||
  !!m.raw?.message?.seenAt ||
  m.seen === true ||
  !!m.seenAt;

const getSeenStatus = (m, isSent) => {
  if (!isSent)        return null;
  if (m._scheduled)   return null;
  if (isMsgSeen(m))   return "seen";
  if (m._optimistic)  return "sent";
  return "delivered";
};

const PinIcon = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" className="opacity-60">
    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
  </svg>
);

const ClockIcon = ({ size = 10 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
  </svg>
);

/* ─────────────────────────────────────────────────────────────────────────────
   ScheduledBadge
─────────────────────────────────────────────────────────────────────────────── */
function ScheduledBadge({ scheduledAt, onCancel, formatScheduledTime }) {
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const ms = new Date(scheduledAt) - Date.now();
  const label = ms <= 0 ? "Sending…" : (formatScheduledTime ? formatScheduledTime(scheduledAt) : "…");

  return (
    <span
      className="
        inline-flex items-center gap-1
        text-[10px] leading-none select-none whitespace-nowrap
        text-amber-700 dark:text-amber-300
      "
      title="Scheduled message — tap × to cancel"
    >
      <ClockIcon size={10} />
      <span>Scheduled</span>
      <span className="tabular-nums font-medium">• {label}</span>
      {onCancel && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          className="
            ml-0.5 flex items-center justify-center
            w-3.5 h-3.5 rounded-full
            bg-amber-200 dark:bg-amber-700
            text-amber-800 dark:text-amber-100
            hover:bg-red-200 dark:hover:bg-red-700
            hover:text-red-800 dark:hover:text-red-100
            transition-colors touch-manipulation
          "
          aria-label="Cancel scheduled message"
        >
          <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      )}
    </span>
  );
}

/* ─── Avatar bubble ──────────────────────────────────────────────────────── */
const AvatarBubble = ({ name, imageUrl }) => {
  const initials = name
    ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const colours = [
    "#e57373","#f06292","#ba68c8","#7986cb",
    "#64b5f6","#4db6ac","#81c784","#ffb74d","#ff8a65",
  ];
  const colour = colours[(name?.charCodeAt(0) || 0) % colours.length];

  return imageUrl ? (
    <img
      src={imageUrl}
      alt={name}
      className="w-7 h-7 rounded-full object-cover flex-shrink-0"
    />
  ) : (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold select-none"
      style={{ backgroundColor: colour }}
    >
      {initials}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TimeTail
─────────────────────────────────────────────────────────────────────────────── */
function TimeTail({ timeLabel, isSent, seenStatus, isPinned, isEdited, isScheduled, scheduledAt, onCancelScheduled, formatScheduledTime }) {


 const spacerW = 42 + (isSent ? 20 : 0) + (isPinned ? 16 : 0) + (isEdited ? 28 : 0);

  return (
    <>
      <span
        aria-hidden="true"
        style={{
          display:      "inline-block",
          float:        "right",
          clear:        "both",
          width:        spacerW,
         height:       15,
          marginLeft:   4,
          marginBottom: -4,
        }}
      />
      <span
        className={[
          "absolute bottom-[4px] right-[7px]",
          "flex items-center gap-[3px]",
         "text-[11px] leading-none select-none pointer-events-none whitespace-nowrap",
          isSent
            ? "text-[#667781] dark:text-neutral-400"
            : "text-[#667781] dark:text-neutral-500",
        ].join(" ")}
        style={{ pointerEvents: isScheduled ? "auto" : "none" }}
      >
        {isScheduled ? (
          <ScheduledBadge
            scheduledAt={scheduledAt}
            onCancel={onCancelScheduled}
            formatScheduledTime={formatScheduledTime}
          />
        ) : (
          <>
            {isPinned && <PinIcon />}
            {isEdited && (
              <span className="italic opacity-70">edited</span>
            )}
            <span>{timeLabel}</span>
           {isSent && <Ticks status={seenStatus} />}
          </>
        )}
      </span>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   VoiceMessageBubble
─────────────────────────────────────────────────────────────────────────────── */
function VoiceMessageBubble({ url, isSent, timeLabel, seenStatus, isPinned, isScheduled, scheduledAt, onCancelScheduled, formatScheduledTime }) {
  const audioRef        = useRef(null);
  const animFrameRef    = useRef(null);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [duration,    setDuration]    = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoaded,    setIsLoaded]    = useState(false);
  const [hasError,    setHasError]    = useState(false);

  const waveform = useRef(
    Array.from({ length: 30 }, (_, i) => {
      const seed = (i * 7 + 13) % 17;
      return 0.15 + (seed / 17) * 0.85;
    })
  );

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
    if (!audio.paused) animFrameRef.current = requestAnimationFrame(updateProgress);
  }, []);

  useEffect(() => () => {
    cancelAnimationFrame(animFrameRef.current);
    audioRef.current?.pause();
  }, []);

  const handleLoadedMetadata = () => {
    const d = audioRef.current?.duration;
    setDuration(isFinite(d) ? d : 0);
    setIsLoaded(true);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
    cancelAnimationFrame(animFrameRef.current);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;
    document.querySelectorAll("audio").forEach((a) => { if (a !== audio && !a.paused) a.pause(); });
    if (audio.paused) {
      audio.play()
        .then(() => { setIsPlaying(true); animFrameRef.current = requestAnimationFrame(updateProgress); })
        .catch(() => setHasError(true));
    } else {
      audio.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animFrameRef.current);
    }
  };

  const handleScrub = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(e.clientX - rect.left, rect.width)) / rect.width;
    audio.currentTime = pct * duration;
    setCurrentTime(audio.currentTime);
  };

  const progress      = duration > 0 ? currentTime / duration : 0;
  const durationLabel = isPlaying || currentTime > 0 ? formatDuration(currentTime) : formatDuration(duration);
  const waveActive    = "#128C7E";
 const waveIdle      = isSent ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.18)";

  return (
    <div className="flex flex-col gap-0.5 min-w-[220px] max-w-[260px] py-0.5">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => { setHasError(true); setIsLoaded(false); }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      <div className="flex items-center gap-2">
        <button
          onClick={togglePlay}
          disabled={hasError}
          className={[
            "flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full transition-all active:scale-95",
            hasError ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
            isSent
              ? "bg-[#128C7E] hover:bg-[#0e7066] text-white"
              : "bg-[#25D366] hover:bg-[#1ebe59] text-white",
          ].join(" ")}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div
          className="flex-1 flex items-center gap-[2px] h-8 cursor-pointer select-none"
          onClick={handleScrub}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
        >
          {waveform.current.map((height, i) => (
            <div
              key={i}
              className="rounded-full flex-1 transition-colors duration-75"
              style={{
                height:          Math.max(3, height * 28),
                backgroundColor: (i / waveform.current.length) <= progress ? waveActive : waveIdle,
                minWidth:        3,
              }}
            />
          ))}
        </div>

        <div className="flex-shrink-0 opacity-40 text-neutral-500">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
          </svg>
        </div>
      </div>

      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[11px] font-medium tabular-nums leading-none text-[#667781] dark:text-neutral-400">
          {hasError ? "Cannot play" : isLoaded ? durationLabel : "Loading…"}
        </span>
        <span className="flex items-center gap-[3px] text-[10px] leading-none select-none whitespace-nowrap">
          {isScheduled ? (
            <ScheduledBadge
              scheduledAt={scheduledAt}
              onCancel={onCancelScheduled}
              formatScheduledTime={formatScheduledTime}
            />
          ) : (
            <>
               <span className="text-[#667781] dark:text-neutral-400">{isPinned && <PinIcon />}{timeLabel}</span>
              {isSent && <Ticks status={seenStatus} />} 
            </>
          )}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TombstoneBubble
─────────────────────────────────────────────────────────────────────────────── */
function TombstoneBubble({ isSent, timeLabel, tombstoneText }) {
  return (
    <div
      className={[
        "relative px-3 py-2 rounded-lg max-w-[65%] sm:max-w-[60%]",
        isSent
          ? "bg-[#DCF8C6] dark:bg-[#005C4B] text-neutral-500 dark:text-neutral-400 shadow-sm ring-1 ring-black/5 dark:ring-white/5 rounded-br-sm"
          : "bg-white dark:bg-[#202C33] text-neutral-500 dark:text-neutral-400 border border-black/10 shadow-sm ring-1 ring-black/5 dark:ring-white/5 rounded-bl-sm",
      ].join(" ")}
    >
      <div className="flex items-center gap-1.5">
        <svg viewBox="0 0 17 17" width="15" height="15" fill="currentColor" className="flex-shrink-0 opacity-60">
          <path d="M8.5 1a7.5 7.5 0 1 0 0 15A7.5 7.5 0 0 0 8.5 1zm0 1.5a6 6 0 0 1 4.37 10.12L3.38 4.13A6 6 0 0 1 8.5 2.5zm0 12a6 6 0 0 1-4.37-10.12l9.49 9.49A6 6 0 0 1 8.5 14.5z" />
        </svg>
        <span className="text-sm italic leading-snug">{tombstoneText}</span>
      </div>
      <div className="mt-0.5 flex items-center justify-end text-[10px] select-none text-neutral-400 dark:text-neutral-500">
        {timeLabel}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   renderTextWithMentions
─────────────────────────────────────────────────────────────────────────────── */
function renderTextWithMentions(text, onMentionClick, renderTextWithLinks) {
  if (!text || typeof text !== "string") return null;

  const parts = text.split(/(@\w+)/g);

  return parts.map((part, i) => {
    if (/^@\w+$/.test(part)) {
      const handle = part.slice(1);
      return (
        <span
          key={i}
          className="
            text-blue-600 dark:text-blue-400
            font-medium cursor-pointer
            hover:underline active:opacity-70
            touch-manipulation
          "
          onClick={(e) => {
            e.stopPropagation();
            onMentionClick(handle);
          }}
        >
          {part}
        </span>
      );
    }
    if (renderTextWithLinks && part) {
      return (
        <span
          key={i}
          dangerouslySetInnerHTML={{ __html: renderTextWithLinks(part) }}
        />
      );
    }
    return part ? <span key={i}>{part}</span> : null;
  });
}

/* ══════════════════════════════════════════════════════════════════════════════
   MessageList
══════════════════════════════════════════════════════════════════════════════ */
export default function MessageList({
  messages,
  selectedItem,
  myChatId,
    chatMessages = null,
  chatKey = null,
  getUserKey,
  getGroupKey, // keep this one only
    setActiveTab,
p2pOrders,
  onConfirmPayment,
  users,
   isActive = true,
      unreadDividerRef = null,
   myName,  
  groupMembers = {},
  selectedMessages = [],
  mobileView = false,
  selectionMode = false,

  onRowTouchStart = () => {},
  onRowTouchMove = () => {},
  onRowTouchEnd = () => {},
  onRowTouchCancel = () => {},

  toggleMessageSelect = () => {},
  handleMouseDown = () => {},
  handleMouseUp = () => {},
  handleMouseLeave = () => {},

  onMessageReply = () => {},
  onMessageForward = () => {},
  onMessageCopy = () => {},
  onMessageEdit = () => {},
  onMessageDelete = () => {},
  onMessageStar = () => {},
  onMessagePin = () => {},
  onMessageUnpin = () => {},
  pinnedMsgIds = [],
onRevealMedia = () => {},
  resolveSenderChatId,
  findUsernameByChatId = () => "",
  renderSystemText = (t="") => t,
  renderTextWithLinks = (t="") => t,

  onOpenMedia = () => {},
  chatBodyRef,

  scheduled = [],
  formatScheduledTime,
  onCancelScheduled = () => {},

  onOpenGroup,
  onOpenDirectMessage,

  socket,

  // added props
  setMessages,
  setGroups,
  uuid,
}) {
  const [mentionSheet, setMentionSheet] = useState({
    open: false,
    user: null,
    mutualGroups: [],
  });

  const snapshotKeyRef    = useRef(null);
  const snapshotMsgIdRef  = useRef(null);   // id of first unread msg (NOT an index)
  const snapshotCountRef  = useRef(0);
  const appliedAnchorRef  = useRef(null);
  const consumedUnreadSnapshotRef = useRef(new Set());
  const wasActiveRef = useRef(false);

 /* ─────────────────────────────────────────────
   Active conversation supplied by ChatWindow.
   Falls back to the old lookup only if needed.
───────────────────────────────────────────── */

const resolvedChatKey =
  chatKey ??
  (selectedItem
    ? selectedItem.type === "user"
      ? getUserKey(selectedItem.chatId)
      : getGroupKey(selectedItem.groupId)
    : null);

const isGroupChat = selectedItem?.type === "group";
const rawList =
  chatMessages ??
  (resolvedChatKey
    ? messages?.[resolvedChatKey] || []
    : []);

const filtered = React.useMemo(() => {
  const sorted = [...rawList].sort(
    (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
  );

  return sorted.filter((m) => {
    if (m.system) return true;
    if (m.messageType === "system") return true;
    if (m.isDeleted) return true;

    const hiddenFor = m.raw?.message?.hiddenFor || m.deletedFor || [];
    return !hiddenFor.includes(myChatId);
  });
}, [rawList, myChatId]);
  const scheduledMap = React.useMemo(() => {
  const map = {};
  for (const s of scheduled) {
    map[s.msgId] = s.scheduledAt;
  }
  return map;
}, [scheduled]);

  const resolveSender = (m) => {
    if (!m) return null;
    if (m.sender)                  return String(m.sender);
    if (m.senderChatId)            return String(m.senderChatId);
    if (m.raw?.key?.participant)   return String(m.raw.key.participant);
    if (m.raw?.key?.senderChatId)  return String(m.raw.key.senderChatId);
    if (m.raw?.key?.fromMe)        return String(myChatId);
    return null;
  };

  const resolveIsSent = (m, senderId) => {
    // ── Prefer a LIVE comparison against the current myChatId first ──────
    // The `fromMe` flag on a message (and m.raw.key.fromMe) can be baked in
    // at decrypt time — e.g. when historical/IDB messages are hydrated on
    // app load, before the user's own chatId has fully settled. That stale
    // flag can say "not mine" for a message that IS actually mine, showing
    // it on the wrong (receiver) side until something re-renders correctly.
    // Live messages never hit this because they only arrive after the app
    // is fully authenticated, so myChatId is already stable.
    //
    // Comparing senderId against the CURRENT myChatId on every render avoids
    // ever trusting a possibly-stale snapshot — so there's nothing to
    // "self-correct" later; it's right on the very first paint.
    if (senderId) return String(senderId) === String(myChatId);

    // Only fall back to baked-in flags when we truly have no sender id to
    // compare (e.g. a malformed/legacy record).
    if (m.fromMe === true)  return true;
    if (m.fromMe === false) return false;
    if (m.raw?.key?.fromMe === true)  return true;
    if (m.raw?.key?.fromMe === false) return false;
    return false;
  };

  const idOfMsg = (m) => m?.id ?? m?.messageId ?? m?.raw?.key?.msgId;

  // An incoming message the viewer hasn't seen. Judges "mine" with the live
  // sender comparison (same as the bubbles), so a stale `fromMe` flag on
  // IDB-hydrated messages can't turn your own old messages into "unread".
  // System and deleted rows never count.
  const isIncomingUnread = (m) => {
    if (m.system || m.messageType === "system" || m.isDeleted) return false;
    return !resolveIsSent(m, resolveSender(m)) && !isMsgSeen(m);
  };

  // Live view of what is unseen right now. This also covers messages that
  // arrived while the chat was hidden (other tab, background, other chat).
  const liveUnreadIndex = filtered.findIndex(isIncomingUnread);
  const liveUnreadCount =
    liveUnreadIndex >= 0
      ? filtered.slice(liveUnreadIndex).filter(isIncomingUnread).length
      : 0;

  // Authoritative anchor from useChatWindowEffects: the id of the first unseen
  // message and how many were unseen, captured the instant before they were
  // flipped to seen. It's replaced with a new object on every chat open.
  const dividerAnchor = unreadDividerRef?.current?.[resolvedChatKey];

  // 1) First render of this chat with data: seed from the live state.
  //    Don't freeze on an empty list; history often arrives a tick later.
  if (snapshotKeyRef.current !== resolvedChatKey && filtered.length > 0) {
    snapshotKeyRef.current = resolvedChatKey;
    appliedAnchorRef.current = null;
    snapshotMsgIdRef.current =
      liveUnreadIndex >= 0 ? idOfMsg(filtered[liveUnreadIndex]) : null;
    snapshotCountRef.current = liveUnreadCount;
    consumedUnreadSnapshotRef.current.delete(resolvedChatKey);
  }

  // 2) Adopt every NEW anchor, not just the first one. The old code froze on
  //    the first render, so reopening a chat showed a stale divider or none.
  if (dividerAnchor && dividerAnchor !== appliedAnchorRef.current) {
    const anchorIdx = filtered.findIndex(
      (m) => idOfMsg(m) === dividerAnchor.msgId
    );
    if (anchorIdx >= 0) {
      appliedAnchorRef.current = dividerAnchor;
      // Cap by what is actually visible after the anchor (hidden/deleted rows
      // are filtered out of `filtered` but may have been counted upstream).
      const visibleIncoming = filtered
        .slice(anchorIdx)
        .filter(
          (m) =>
            !m.system &&
            m.messageType !== "system" &&
            !m.isDeleted &&
            !resolveIsSent(m, resolveSender(m))
        ).length;
      snapshotMsgIdRef.current = dividerAnchor.msgId;
      snapshotCountRef.current = Math.max(
        1,
        Math.min(dividerAnchor.count, visibleIncoming)
      );
      consumedUnreadSnapshotRef.current.delete(resolvedChatKey);
    }
  }

  // WhatsApp behaviour:
  // First active open -> keep divider.
  // Leave chat -> remember it was shown.
  // Second active open -> remove divider.
  useEffect(() => {
    if (!resolvedChatKey) return;

    const becameActive = isActive && !wasActiveRef.current;
    const becameInactive = !isActive && wasActiveRef.current;

    // User left the chat after seeing the divider once.
    if (
      becameInactive &&
      snapshotCountRef.current > 0 &&
      !consumedUnreadSnapshotRef.current.has(resolvedChatKey)
    ) {
      consumedUnreadSnapshotRef.current.add(resolvedChatKey);
    }

    // User returned to the same chat: clear the old divider. If messages
    // arrived while away, useChatWindowEffects publishes a fresh anchor on
    // this activation and step 2 above adopts it on the next render.
    if (
      becameActive &&
      consumedUnreadSnapshotRef.current.has(resolvedChatKey)
    ) {
      snapshotMsgIdRef.current = null;
      snapshotCountRef.current = 0;
      consumedUnreadSnapshotRef.current.delete(resolvedChatKey);
    }

    wasActiveRef.current = isActive;
  }, [isActive, resolvedChatKey]);

  // 3) Unread arrived later (chat hidden or tab backgrounded, so it was stored
  //    unseen). Start a divider, or grow the count if the same run is still
  //    unseen. The count never shrinks when messages get flipped to seen.
  if (liveUnreadCount > 0) {
    const liveFirstId = idOfMsg(filtered[liveUnreadIndex]);
    if (snapshotCountRef.current === 0 || !snapshotMsgIdRef.current) {
      snapshotMsgIdRef.current = liveFirstId;
      snapshotCountRef.current = liveUnreadCount;
      consumedUnreadSnapshotRef.current.delete(resolvedChatKey);
    } else if (
      liveFirstId === snapshotMsgIdRef.current &&
      liveUnreadCount > snapshotCountRef.current
    ) {
      snapshotCountRef.current = liveUnreadCount;
    }
  }

  // Derive the position from the message id on every render. A stored index
  // drifts when history loads or the list re-sorts, which put the divider at
  // the top even with one unread message.
  const firstUnreadIndex = snapshotMsgIdRef.current
    ? filtered.findIndex((m) => idOfMsg(m) === snapshotMsgIdRef.current)
    : -1;
  const unreadCount = firstUnreadIndex >= 0 ? snapshotCountRef.current : 0;

const pinnedSet = React.useMemo(
  () => new Set(pinnedMsgIds),
  [pinnedMsgIds]
);

  // ── Scroll to bottom (or to the unread divider) whenever the
  //    active chat changes. Without this, the container is left at
  //    scrollTop: 0 by the browser, which is why opening a chat with
  //    no unread badge showed the scrollbar at the top instead of
  //    jumping to the latest messages.
  const didInitialScrollRef = useRef(null);

useLayoutEffect(() => {
  // IMPORTANT:
  // Background ChatWindow instances can remain mounted while hidden.
  // Do not claim the initial scroll until this chat is actually active.
  if (!isActive) return;

  if (!chatBodyRef?.current || !resolvedChatKey) return;
  if (filtered.length === 0) return;

  // Land once per chat open. Anything after that (new messages arriving
  // while the chat is already open) is useChatScroll's job — it checks
  // whether the user has scrolled up before moving anything. Re-running
  // this on every filtered.length change was overriding that check and
  // yanking the view back to the bottom/divider on every new message.
  if (didInitialScrollRef.current === resolvedChatKey) return;
  didInitialScrollRef.current = resolvedChatKey;

  const container = chatBodyRef.current;

  const scroll = () => {
    if (unreadCount > 0) {
      const divider = container.querySelector('[data-unread-divider="true"]');
      if (divider) {
        divider.scrollIntoView({ block: "start" });
        return;
      }
    }

    container.scrollTop = container.scrollHeight;
  };

  scroll();

  // Re-anchor once any avatar/media image finishes loading — images that
  // land after this grow scrollHeight and leave a no-unread chat
  // stranded above the true bottom. Only relevant for this initial landing.
  const imgs = container.querySelectorAll("img");
  const onImgLoad = () => scroll();

  imgs.forEach((img) => {
    if (!img.complete) {
      img.addEventListener("load", onImgLoad);
    }
  });

  return () => {
    imgs.forEach((img) => {
      img.removeEventListener("load", onImgLoad);
    });
  };
}, [
  isActive,
  resolvedChatKey,
  unreadCount,
  filtered.length,
  chatBodyRef,
]);
  const senderColour = (name = "") => {
    const palette = ["#e53935","#8e24aa","#1e88e5","#00897b","#f4511e","#6d4c41","#546e7a","#2e7d32","#ad1457"];
    return palette[(name.charCodeAt(0) || 0) % palette.length];
  };

  const isSystemMessage = (m) => m.system || m.messageType === "system";

  // ── Check if current viewer is an admin in the current group ─────────────
  const viewerIsAdmin = (() => {
    if (!isGroupChat || !selectedItem) return false;
    const members = groupMembers?.[selectedItem.groupId] || [];
    const me = members.find((m) => String(m.chatId) === String(myChatId));
    return !!(me?.isAdmin || me?.isSuperAdmin);
  })();

  // ── Handle @mention tap ───────────────────────────────────────────────────
  const handleMentionClick = useCallback((handle) => {
    if (!isGroupChat || !selectedItem) return;

    const members = groupMembers?.[selectedItem.groupId] || [];

    const member = members.find(
      (m) =>
        (m.userName  || "").toLowerCase() === handle.toLowerCase() ||
        (m.username  || "").toLowerCase() === handle.toLowerCase() ||
        (m.name      || "").toLowerCase() === handle.toLowerCase()
    );

    const userObj = member
      ? (users.find((u) => String(u.chatId) === String(member.chatId)) || member)
      : users.find(
          (u) =>
            (u.userName  || "").toLowerCase() === handle.toLowerCase() ||
            (u.username  || "").toLowerCase() === handle.toLowerCase()
        );

    if (!userObj && !member) return;

    // Merge user + member so we have both profile data and role flags
    const resolvedUser = { ...(member || {}), ...(userObj || {}) };

    setMentionSheet({ open: true, user: resolvedUser, mutualGroups: [] });
  }, [isGroupChat, groupMembers, selectedItem, users]);

 if (!resolvedChatKey) return null;

  return (
    <>
      {filtered.map((m, index) => {
        const prev             = filtered[index - 1];
        const showDaySeparator = !prev || !isSameDay(prev.timestamp, m.timestamp);
        const dayLabel         = formatDayLabel(m.timestamp);
/* ── System message ── */
if (isSystemMessage(m)) {
  const systemMsgId =
    m.messageId ||
    m.msgId ||
    m.id ||
    m.raw?.key?.msgId ||
    `system-${index}`;

  const systemText =
    m.text ||
    m.decryptedText ||
    "";

  return (
    <React.Fragment key={systemMsgId}>
      {showDaySeparator && (
        <div className="sticky top-2 z-10 flex justify-center my-3 pointer-events-none">
          <div className="px-3 py-1 rounded-full text-xs bg-neutral-200/80 text-neutral-700 dark:bg-neutral-700/70 dark:text-neutral-200 backdrop-blur shadow-sm">
            {dayLabel}
          </div>
        </div>
      )}

      <div
        className="mx-auto my-2 max-w-[85%] text-center"
        data-system-event={m.systemEventType || ""}
      >

        {/* ─────────────────────────────
            PAYMENT CONFIRMATION
        ───────────────────────────── */}
        {systemText?.includes(
          "Kindly verify the payment and release the funds if payment has been received."
        ) ? (() => {

          const match =
            systemText.match(
              /Order #([A-Za-z0-9_-]+)/
            );

          const orderId =
            match?.[1];

          const order =
            p2pOrders?.find(
              (o) =>
                String(o.orderId) ===
                String(orderId)
            );

          const isSeller =
            order &&
            String(order.sellerChatId) ===
            String(myChatId);

          const canConfirm =
            isSeller &&
            order?.status === "paid";

          return (
            <div className="space-y-2 rounded-full bg-neutral-200/70 dark:bg-neutral-700/60 px-3 py-2">

              <div
                className="text-xs text-neutral-700 dark:text-neutral-200"
                dangerouslySetInnerHTML={{
                  __html:
                    renderSystemText(
                      systemText
                    ),
                }}
              />

              {canConfirm && (
                <button
                  onClick={() =>
                    onConfirmPayment(order)
                  }
                  className="
                    rounded-lg
                    bg-green-600
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    text-white
                    hover:bg-green-700
                  "
                >
                  ✅ Confirm Payment
                </button>
              )}

            </div>
          );

        })() : systemText?.includes(
          "Tap the Assets tab below to check your balance."
        ) ? (

          /* ─────────────────────────────
              CHECK BALANCE
          ───────────────────────────── */
          <div className="space-y-2 rounded-full bg-neutral-200/70 dark:bg-neutral-700/60 px-3 py-2">

            <div
              className="text-xs text-neutral-700 dark:text-neutral-200"
              dangerouslySetInnerHTML={{
                __html: renderSystemText(
                  systemText.replace(
                    "\n\nTap the Assets tab below to check your balance.",
                    ""
                  )
                ),
              }}
            />

            <button
              onClick={() => {
                if (
                  typeof setActiveTab ===
                  "function"
                ) {
                  setActiveTab(
                    TABS.ASSETS
                  );
                }
              }}
              className="
                rounded-lg
                bg-green-600
                px-4
                py-2
                text-sm
                font-semibold
                text-white
                hover:bg-green-700
              "
            >
              💰 Check Balance
            </button>

          </div>

        ) : (

          <div
            className="
              rounded-full
              bg-neutral-200/70
              dark:bg-neutral-700/60
              px-3
              py-1
              text-xs
              text-neutral-700
              dark:text-neutral-200
            "
            dangerouslySetInnerHTML={{
              __html:
                renderSystemText(
                  systemText
                ),
            }}
          />

        )}

      </div>
    </React.Fragment>
  );
}
      const senderId =
  typeof resolveSenderChatId === "function"
    ? resolveSenderChatId(m)
    : resolveSender(m) || m.senderChatId;

const isSent = resolveIsSent(m, senderId);
const msgId = m.messageId || m.id || m.raw?.key?.msgId;
const timeLabel = formatTime(m.timestamp);

const prevNonSystem = filtered
  .slice(0, index)
  .reverse()
  .find((mm) => !isSystemMessage(mm));
const handleCancelThisScheduled = () => {
  onCancelScheduled(msgId);
};
        return (
          <React.Fragment key={msgId}>
            {showDaySeparator && (
              <div className="sticky top-2 z-10 flex justify-center my-3 pointer-events-none">
              <div className="px-3 py-[5px] rounded-lg text-[12.5px] bg-white text-[#54656f] dark:bg-[#182229] dark:text-[#8696a0] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
                  {dayLabel}
                </div>
              </div>
            )}

            {firstUnreadIndex >= 0 &&
              index === firstUnreadIndex &&
              unreadCount > 0 && (
                <div
                  data-unread-divider="true"
                  className="flex items-center gap-2 my-3 px-2 pointer-events-none select-none"
                >
                  <div className="flex-1 h-px bg-green-500/50 dark:bg-green-400/40" />
                  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 shadow-sm whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 text-white text-[10px] font-bold leading-none">
                      {unreadCount}
                    </span>
                    unread message{unreadCount !== 1 ? "s" : ""}
                  </div>
                  <div className="flex-1 h-px bg-green-500/50 dark:bg-green-400/40" />
                </div>
              )}

          <MessageRow
  message={m}
  previousMessage={prevNonSystem}
  myChatId={myChatId}
  isGroupChat={isGroupChat}
  selectedItem={selectedItem}
  users={users}
  groupMembers={groupMembers}
  resolveSenderChatId={resolveSenderChatId}
  findUsernameByChatId={findUsernameByChatId}
  senderColour={senderColour}
  pinnedSet={pinnedSet}
  scheduledMap={scheduledMap}
  selectedMessages={selectedMessages}
  mobileView={mobileView}
  onRevealMedia={onRevealMedia}
  onOpenMedia={onOpenMedia}
  onRowTouchStart={onRowTouchStart}
  onRowTouchMove={onRowTouchMove}
  onRowTouchEnd={onRowTouchEnd}
  onRowTouchCancel={onRowTouchCancel}
  handleMouseDown={handleMouseDown}
  handleMouseUp={handleMouseUp}
  handleMouseLeave={handleMouseLeave}
  renderTextWithLinks={renderTextWithLinks}
  renderTextWithMentions={renderTextWithMentions}
  handleMentionClick={handleMentionClick}
  TimeTail={TimeTail}
  VoiceMessageBubble={VoiceMessageBubble}
  AvatarBubble={AvatarBubble}
   ScheduledBadge={ScheduledBadge}
  onMessageForward={onMessageForward}
  handleCancelScheduled={handleCancelThisScheduled}
/>
          </React.Fragment>
        );
      })}

      {/* ── @mention bottom sheet ── */}
       <MentionUserSheet
        open={mentionSheet.open}
        onClose={() => setMentionSheet((s) => ({ ...s, open: false }))}
        mentionedUser={mentionSheet.user}
        myChatId={myChatId}
          myName={myName}   
        isViewerAdmin={viewerIsAdmin}
        mutualGroups={mentionSheet.mutualGroups}
        onOpenGroup={onOpenGroup}
        onOpenDirectMessage={onOpenDirectMessage}
        socket={socket}
        groupId={selectedItem?.groupId}
        setMessages={setMessages}
        setGroups={setGroups}
        getGroupKey={getGroupKey}
        uuid={uuid}
      />
    </>
  );
}